import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, interval } from 'rxjs';
import {
  ADVANCED_SEARCH_CUSTOM_MAX_COUNT,
  ADVANCED_SEARCH_CUSTOM_MIN_COUNT,
  ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MAX,
  ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MIN,
  ADVANCED_SEARCH_CUSTOM_TEMPERATURE_STEP,
  ADVANCED_SEARCH_CUSTOM_SYSTEM_PROMPT_MAX_LENGTH,
  ADVANCED_SEARCH_RENDERING_MODEL_OPTIONS,
  ADVANCED_SEARCH_PLANNER_MODEL_OPTIONS,
  AdvancedSearchRenderingModel,
  AdvancedSearchPlannerModel,
  ADVANCED_SEARCH_GENERATION_MODE_OPTIONS,
  AdvancedSearchGenerationMode,
  getAdvancedSearchRenderingModelLabel,
  getAdvancedSearchPlannerModelLabel,
  getAdvancedSearchGenerationModeLabel,
} from 'src/app/models/advanced-search';
import { SettingsService } from 'src/app/shared/providers/settings.service';
import { UtilityFunctionsService } from 'src/app/shared/providers/utility-functions.service';

export interface WebAdvancedSearchDevPairBatchState {
  eligibleCount: number;
  failedCount: number;
  total: number;
  done: number;
  success: number;
  inProgress: boolean;
  stopRequested: boolean;
  progressLabel: string;
}

export interface WebAdvancedSearchDevData {
  routeAdvancedSearchGenerationModeOverride: AdvancedSearchGenerationMode | null;
  getGenerationModeValidationState: () => {
    isValid: boolean;
    rawMode: string | null;
    source: 'route' | 'storage' | null;
  };
  resetGenerationModeToDefault: () => void;
  getPairBatchState: () => WebAdvancedSearchDevPairBatchState;
  generatePairPreviews: () => void;
  retryFailedPairPreviews: () => void;
  stopPairBatch: () => void;
  pairPreviewDebugModeChanged: () => void;
  canSaveAiCollection: () => boolean;
  saveAiCollection: () => Promise<{ collectionId: string; savedCount: number } | null>;
}

@Component({
  selector: 'app-web-advanced-search-dev',
  templateUrl: './web-advanced-search-dev.component.html',
  styleUrls: ['./web-advanced-search-dev.component.scss']
})
export class WebAdvancedSearchDevComponent implements OnInit, OnDestroy {
  public readonly generationModes = ADVANCED_SEARCH_GENERATION_MODE_OPTIONS;
  public readonly renderingModelOptions = ADVANCED_SEARCH_RENDERING_MODEL_OPTIONS;
  public readonly plannerModelOptions = ADVANCED_SEARCH_PLANNER_MODEL_OPTIONS;
  public readonly customAssetCountMin = ADVANCED_SEARCH_CUSTOM_MIN_COUNT;
  public readonly customAssetCountMax = ADVANCED_SEARCH_CUSTOM_MAX_COUNT;
  public readonly customTemperatureMin = ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MIN;
  public readonly customTemperatureMax = ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MAX;
  public readonly customTemperatureStep = ADVANCED_SEARCH_CUSTOM_TEMPERATURE_STEP;
  public readonly customSystemPromptMaxLength = ADVANCED_SEARCH_CUSTOM_SYSTEM_PROMPT_MAX_LENGTH;
  public pairBatchState: WebAdvancedSearchDevPairBatchState = this.buildEmptyPairBatchState();
  public generationModeValidationState: {
    isValid: boolean;
    rawMode: string | null;
    source: 'route' | 'storage' | null;
  } = {
    isValid: true,
    rawMode: null,
    source: null,
  };
  public isAiCollectionSaveInProgress: boolean = false;

  private stateRefreshSubscription?: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: WebAdvancedSearchDevData,
    public settingsService: SettingsService,
    public utilityService: UtilityFunctionsService,
    private dialogRef: MatDialogRef<WebAdvancedSearchDevComponent>,
  ) {}

  ngOnInit(): void {
    this.refreshPairBatchState();
    this.refreshGenerationModeValidationState();
    this.stateRefreshSubscription = interval(500).subscribe(() => {
      this.refreshPairBatchState();
      this.refreshGenerationModeValidationState();
    });
  }

  ngOnDestroy(): void {
    this.stateRefreshSubscription?.unsubscribe();
  }

  public get savedAdvancedSearchGenerationMode(): AdvancedSearchGenerationMode {
    return this.settingsService.advancedSearchGenerationMode;
  }

  public get routeAdvancedSearchGenerationModeOverride(): AdvancedSearchGenerationMode | null {
    return this.data.routeAdvancedSearchGenerationModeOverride;
  }

  public get effectiveAdvancedSearchGenerationMode(): AdvancedSearchGenerationMode {
    return this.routeAdvancedSearchGenerationModeOverride || this.savedAdvancedSearchGenerationMode;
  }

  public get shouldShowCustomSettings(): boolean {
    return this.savedAdvancedSearchGenerationMode === 'custom';
  }

  public get customAssetCount(): number {
    return this.settingsService.advancedSearchCustomAssetCount;
  }

  public get customSystemPrompt(): string {
    return this.settingsService.advancedSearchCustomSystemPrompt;
  }

  public get customPortraitTemperature(): number {
    return this.settingsService.advancedSearchCustomPortraitTemperature;
  }

  public get customLandscapeTemperature(): number {
    return this.settingsService.advancedSearchCustomLandscapeTemperature;
  }

  public get canGeneratePairPreviews(): boolean {
    return !this.pairBatchState.inProgress && this.pairBatchState.eligibleCount > 0;
  }

  public get canRetryFailedPairPreviews(): boolean {
    return !this.pairBatchState.inProgress && this.pairBatchState.failedCount > 0;
  }

  public get canStopPairBatch(): boolean {
    return this.pairBatchState.inProgress && !this.pairBatchState.stopRequested;
  }

  public get pairPreviewDebugMode(): boolean {
    return this.settingsService.advancedSearchPairPreviewDebugMode;
  }

  public get canSaveAiCollection(): boolean {
    if (this.isAiCollectionSaveInProgress) {
      return false;
    }

    return !!this.data.canSaveAiCollection?.();
  }

  public get savedRenderingModel(): AdvancedSearchRenderingModel {
    return this.settingsService.advancedSearchRenderingModel;
  }

  public get savedPlannerModel(): AdvancedSearchPlannerModel {
    return this.settingsService.advancedSearchPlannerModel;
  }

  public setAdvancedSearchGenerationMode(mode: AdvancedSearchGenerationMode): void {
    if (this.savedAdvancedSearchGenerationMode === mode) {
      return;
    }

    this.settingsService.advancedSearchGenerationMode = mode;
    this.utilityService.showSnackBar(`Advanced Search set to ${this.getAdvancedSearchGenerationModeLabel(mode)} mode.`, 2500);
    this.refreshGenerationModeValidationState();
  }

  public getAdvancedSearchGenerationModeLabel(mode: AdvancedSearchGenerationMode): string {
    return getAdvancedSearchGenerationModeLabel(mode);
  }

  public getAdvancedSearchRenderingModelLabel(model: AdvancedSearchRenderingModel): string {
    return getAdvancedSearchRenderingModelLabel(model);
  }

  public getAdvancedSearchPlannerModelLabel(model: AdvancedSearchPlannerModel): string {
    return getAdvancedSearchPlannerModelLabel(model);
  }

  public setRenderingModel(model: AdvancedSearchRenderingModel): void {
    if (this.savedRenderingModel === model) {
      return;
    }

    this.settingsService.advancedSearchRenderingModel = model;
    this.utilityService.showSnackBar(`Rendering model set to ${this.getAdvancedSearchRenderingModelLabel(model)}.`, 2500);
  }

  public setPlannerModel(model: AdvancedSearchPlannerModel): void {
    if (this.savedPlannerModel === model) {
      return;
    }

    this.settingsService.advancedSearchPlannerModel = model;
    this.utilityService.showSnackBar(`Planner model set to ${this.getAdvancedSearchPlannerModelLabel(model)}.`, 2500);
  }

  public onCustomAssetCountInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const nextCount = Number(inputElement?.value);
    this.settingsService.advancedSearchCustomAssetCount = nextCount;
  }

  public onCustomSystemPromptInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.settingsService.advancedSearchCustomSystemPrompt = textarea?.value || '';
  }

  public onCustomPortraitTemperatureInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.settingsService.advancedSearchCustomPortraitTemperature = Number(inputElement?.value);
  }

  public onCustomLandscapeTemperatureInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.settingsService.advancedSearchCustomLandscapeTemperature = Number(inputElement?.value);
  }

  public onResetInvalidGenerationModeClick(): void {
    this.data.resetGenerationModeToDefault?.();
    this.refreshGenerationModeValidationState();
  }

  public onGeneratePairPreviewsClick(): void {
    if (!this.canGeneratePairPreviews) {
      return;
    }

    this.data.generatePairPreviews();
    this.refreshPairBatchState();
  }

  public onRetryFailedPairPreviewsClick(): void {
    if (!this.canRetryFailedPairPreviews) {
      return;
    }

    this.data.retryFailedPairPreviews();
    this.refreshPairBatchState();
  }

  public onStopPairBatchClick(): void {
    if (!this.canStopPairBatch) {
      return;
    }

    this.data.stopPairBatch();
    this.refreshPairBatchState();
  }

  public onPairPreviewDebugModeChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.settingsService.advancedSearchPairPreviewDebugMode = !!inputElement.checked;
    this.data.pairPreviewDebugModeChanged?.();
  }

  public async onSaveAiCollectionClick(): Promise<void> {
    if (!this.canSaveAiCollection) {
      return;
    }

    this.isAiCollectionSaveInProgress = true;
    try {
      const result = await this.data.saveAiCollection?.();
      if (result?.collectionId) {
        this.utilityService.showSnackBar(
          `AI collection saved: ${result.collectionId} (${result.savedCount} assets).`,
          4000,
        );
      }
    } catch (error) {
      this.utilityService.importantError('AI collection save failed', error, {
        duration: 12000,
      });
    } finally {
      this.isAiCollectionSaveInProgress = false;
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  private refreshPairBatchState(): void {
    this.pairBatchState = this.data.getPairBatchState?.() || this.buildEmptyPairBatchState();
  }

  private refreshGenerationModeValidationState(): void {
    this.generationModeValidationState = this.data.getGenerationModeValidationState?.() || {
      isValid: true,
      rawMode: null,
      source: null,
    };
  }

  private buildEmptyPairBatchState(): WebAdvancedSearchDevPairBatchState {
    return {
      eligibleCount: 0,
      failedCount: 0,
      total: 0,
      done: 0,
      success: 0,
      inProgress: false,
      stopRequested: false,
      progressLabel: 'Pair previews: 0/0',
    };
  }
}
